import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";

const MAX_CHARMS = 5;

export default function CharmBuilder() {
  const [bases, setBases] = useState([]);
  const [charms, setCharms] = useState([]);
  const [base, setBase] = useState(null);
  const [picked, setPicked] = useState([]); // array of product ids
  const [loaded, setLoaded] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const isBracelet = (p) => /bracelet|bangle|kada|anklet/i.test(p.category || "") || /bracelet|anklet/i.test(p.title || "");
    const isCharm = (p) => (p.tags || []).includes("charm") || /charm/i.test(p.title || "") || /charm/i.test(p.category || "");
    Promise.all([
      api.get("/products", { params: { limit: 48 } }).catch(() => ({ data: { items: [] } })),
      api.get("/products", { params: { limit: 48, q: "charm" } }).catch(() => ({ data: { items: [] } })),
    ]).then(([b, c]) => {
      const all = b.data.items || [];
      const filteredBases = all.filter(isBracelet);
      setBases(filteredBases);
      if (filteredBases.length && !base) setBase(filteredBases[0]);
      const charmHits = c.data.items || [];
      setCharms(charmHits.filter(isCharm).length ? charmHits.filter(isCharm) : charmHits);
      setLoaded(true);
    });
  }, []);

  const total = useMemo(() => {
    const basePrice = base?.price_min || 0;
    return basePrice + picked.reduce((s, id) => s + (charms.find((c) => c.id === id)?.price_min || 0), 0);
  }, [base, picked, charms]);

  const totalWeight = useMemo(() => {
    const baseWt = (base?.variants?.[0]?.net_weight) || 4.5;
    const charmWt = picked.reduce((s, id) => {
      const c = charms.find((x) => x.id === id);
      return s + ((c?.variants?.[0]?.net_weight) || 1.1);
    }, 0);
    return (baseWt + charmWt).toFixed(2);
  }, [base, picked, charms]);

  const toggle = (id) => {
    setPicked((p) => {
      if (p.includes(id)) return p.filter((x) => x !== id);
      if (p.length >= MAX_CHARMS) { toast.error(`Up to ${MAX_CHARMS} charms on one bracelet`); return p; }
      return [...p, id];
    });
  };

  const removeCharm = (id) => {
    setPicked((p) => p.filter((x) => x !== id));
  };

  const build = () => {
    if (!base) return toast.error("Pick a base bracelet first");
    const charmObjects = picked.map((id) => charms.find((c) => c.id === id)).filter(Boolean);
    const names = charmObjects.map((c) => c.title);
    const v = {
      id: "byo-charm-bracelet",
      sku: `BYO-${base.id.slice(-4)}-${picked.length}C`,
      options: { Composition: `${picked.length} charms on ${base.title}` },
      stock: 50,
      net_weight: Number(totalWeight),
      pricing: { subtotal: total, display_price: total, overridden: true, final: total },
    };
    const pers = {
      selections: {},
      summary: `Base: ${base.title}${names.length ? ` + Charms: ${names.join(", ")}` : ""}`,
      surcharge: 0,
      charms: names,
    };
    addItem({ ...base, title: `Custom Charm Bracelet — ${base.title}`, rate_info: { gst_rate: 3 } }, v, 1, pers);
    toast.success("Your custom charm bracelet was added to cart");
    navigate("/cart");
  };

  return (
    <div className="container-x py-12" data-testid="charm-builder-page">
      <div className="max-w-3xl">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Build Your Own</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">The Charm Bar</h1>
        <p className="text-stone-600 mt-3 leading-relaxed">
          Select a solid 18K gold chain base, then attach up to {MAX_CHARMS} meaningful charms. Every piece is hallmarked,
          assembled in our atelier, and priced transparently by live gold weight and making.
        </p>
      </div>

      {/* Visual Interactive Strap Simulation */}
      <div className="mt-8 border border-gold/40 rounded-2xl bg-gradient-to-b from-[#FDFBF7] to-[#F5EFE6] p-6 sm:p-8 shadow-sm">
        <div className="flex items-center justify-between border-b border-stone-200 pb-4 flex-wrap gap-2">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-brand font-semibold">Atelier Preview</p>
            <p className="text-sm font-heading font-medium text-ink">{base ? base.title : "Select a bracelet base below"}</p>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono">
            <span className="text-stone-500">Est. Weight: <strong className="text-brand">{totalWeight}g</strong> (18K)</span>
            <span className="text-stone-300">|</span>
            <span className="text-gold font-semibold">{picked.length}/{MAX_CHARMS} Charms</span>
          </div>
        </div>

        {/* Visual Chain & Suspended Charms */}
        <div className="py-10 relative flex flex-col items-center justify-center">
          {/* Metallic chain line */}
          <div className="w-full max-w-2xl h-3 rounded-full bg-gradient-to-r from-[#C9A227] via-[#F3DFA0] to-[#C9A227] shadow-inner relative flex items-center justify-between px-6">
            <span className="h-4 w-4 rounded-full bg-brand border-2 border-gold shadow-sm -ml-3" title="Clasp" />
            <span className="h-4 w-4 rounded-full bg-brand border-2 border-gold shadow-sm -mr-3" title="Link loop" />
          </div>

          {/* Suspended Charm Drop Nodes */}
          <div className="w-full max-w-2xl flex justify-around mt-1 px-4 min-h-[110px]">
            {Array.from({ length: MAX_CHARMS }).map((_, slotIdx) => {
              const charmId = picked[slotIdx];
              const charm = charmId ? charms.find((c) => c.id === charmId) : null;
              return (
                <div key={slotIdx} className="flex flex-col items-center relative group" data-testid={`charm-slot-${slotIdx}`}>
                  {/* Jump ring line */}
                  <div className={`w-0.5 h-6 ${charm ? "bg-gold" : "bg-stone-300 border-dashed"}`} />
                  {charm ? (
                    <div className="relative mt-1 animate-in fade-in zoom-in-95 duration-200">
                      <div className="h-14 w-14 rounded-full border-2 border-gold bg-white shadow-md p-1 overflow-hidden flex items-center justify-center hover:scale-105 transition-transform">
                        {(charm.images || [])[0] ? (
                          <img src={charm.images[0]} alt={charm.title} className="h-full w-full object-contain rounded-full" />
                        ) : (
                          <span className="font-heading text-xs text-brand">✦</span>
                        )}
                      </div>
                      <button
                        onClick={() => removeCharm(charm.id)}
                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-brand text-ivory text-[10px] flex items-center justify-center shadow hover:bg-red-600 transition-colors"
                        title="Remove charm"
                        data-testid={`remove-charm-${charm.slug}`}
                      >
                        <X size={10} />
                      </button>
                      <p className="text-[10px] text-center font-medium max-w-[70px] truncate mt-1 text-ink">{charm.title.replace(/ charm/i, "")}</p>
                    </div>
                  ) : (
                    <div className="mt-1 h-14 w-14 rounded-full border-2 border-dashed border-stone-300 bg-white/50 flex flex-col items-center justify-center text-stone-300">
                      <span className="text-[10px] font-mono">Slot {slotIdx + 1}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-8 mt-10">
        <div className="space-y-8">
          {/* Step 1: Base */}
          <section data-testid="byo-bases" className="border border-stone-200 bg-white p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-brand text-ivory text-xs flex items-center justify-center">1</span>
                Choose your Bracelet Base
              </h2>
              <span className="text-xs font-mono text-stone-500">Solid 18K Gold</span>
            </div>
            {!loaded ? (
              <p className="text-sm text-stone-400">Loading bases…</p>
            ) : bases.length === 0 ? (
              <p className="text-sm text-stone-500">No bases found.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {bases.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setBase(p)}
                    className={`border text-left p-3 rounded-lg transition-all ${
                      base?.id === p.id
                        ? "border-brand bg-brand/5 ring-1 ring-brand shadow-sm"
                        : "border-stone-200 bg-white hover:border-stone-400"
                    }`}
                    data-testid={`byo-base-${p.slug}`}
                  >
                    <div className="aspect-square bg-stone-50 mb-2 rounded overflow-hidden">
                      {(p.images || [])[0] ? (
                        <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand/30 font-heading text-2xl">S</div>
                      )}
                    </div>
                    <p className="text-xs font-medium truncate text-ink">{p.title}</p>
                    <p className="text-xs font-mono text-gold font-semibold mt-0.5">{inr(p.price_min || 0)}</p>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Step 2: Charms */}
          <section data-testid="byo-charms" className="border border-stone-200 bg-white p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-brand text-ivory text-xs flex items-center justify-center">2</span>
                Attach Charms ({picked.length}/{MAX_CHARMS})
              </h2>
              <span className="text-xs font-mono text-stone-500">Tap to toggle</span>
            </div>
            {loaded && charms.length === 0 ? (
              <p className="text-sm text-stone-500">No charms loaded.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {charms.map((c) => {
                  const isPicked = picked.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggle(c.id)}
                      className={`border p-3 text-left rounded-lg transition-all relative ${
                        isPicked
                          ? "border-gold bg-gold/5 ring-1 ring-gold shadow-sm"
                          : "border-stone-200 bg-white hover:border-stone-400"
                      }`}
                      data-testid={`byo-charm-${c.slug}`}
                    >
                      {isPicked && (
                        <span className="absolute top-2 right-2 bg-brand text-ivory text-[9px] font-mono px-1.5 py-0.5 rounded-full">
                          ADDED
                        </span>
                      )}
                      <div className="aspect-square bg-stone-50 mb-2 rounded overflow-hidden">
                        {(c.images || [])[0] ? (
                          <img src={c.images[0]} alt={c.title} className="w-full h-full object-contain p-2" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand/30 font-heading">✦</div>
                        )}
                      </div>
                      <p className="text-xs font-medium truncate text-ink">{c.title}</p>
                      <p className="text-xs font-mono text-gold font-semibold mt-0.5">{inr(c.price_min || 0)}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Aside Summary */}
        <aside className="border border-stone-200 bg-white p-6 rounded-xl h-fit lg:sticky lg:top-24 shadow-sm" data-testid="byo-summary">
          <h3 className="font-heading font-semibold text-lg text-ink">Custom Configuration</h3>
          <dl className="mt-4 space-y-2.5 text-sm divide-y divide-stone-100">
            <div className="flex justify-between pt-1">
              <dt className="text-stone-600">Base Bracelet</dt>
              <dd className="text-right max-w-44 truncate font-medium">{base ? base.title : "—"}</dd>
            </div>
            {picked.map((id) => {
              const c = charms.find((x) => x.id === id);
              return (
                <div key={id} className="flex justify-between items-center pt-2 text-xs">
                  <dt className="text-stone-600 truncate max-w-40 flex items-center gap-1.5">
                    <button onClick={() => removeCharm(id)} className="text-stone-400 hover:text-red-600" title="Remove">
                      <Trash2 size={11} />
                    </button>
                    {c?.title}
                  </dt>
                  <dd className="font-mono text-stone-900">{inr(c?.price_min || 0)}</dd>
                </div>
              );
            })}
            <div className="flex justify-between pt-2 text-xs font-mono text-stone-500">
              <dt>Total 18K Gold Wt</dt>
              <dd>{totalWeight} grams</dd>
            </div>
            <div className="flex justify-between pt-2 text-xs font-mono text-stone-500">
              <dt>GST @ 3%</dt>
              <dd>Included</dd>
            </div>
          </dl>
          <div className="border-t border-stone-200 mt-4 pt-3 flex justify-between font-heading font-semibold text-lg">
            <span>Total</span>
            <span className="text-brand font-bold" data-testid="byo-total">{inr(total)}</span>
          </div>
          <Button
            onClick={build}
            disabled={!base}
            className="w-full mt-5 bg-brand hover:bg-brand-2 text-ivory rounded-full h-11 btn-press font-semibold"
            data-testid="byo-add-btn"
          >
            Add Bracelet to Bag
          </Button>
          <p className="text-[11px] text-stone-500 mt-3 text-center">
            Individually hand-crafted in 7–10 days. <Link to="/materials" className="underline hover:text-brand">Materials & Hallmarking</Link>
          </p>
        </aside>
      </div>
    </div>
  );
}
